import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { ShieldAlert, Skull, Loader2, X } from "lucide-react";

/**
 * 2026-09-20 — Global Emergency Kill Switch (chat: "Risk Checks and
 * Governance... Hard (Global Emergency Kill Switch - Manual - Button in
 * Live Orders)... GLOBAL KILL, STOP ALL NEW ORDERS, CANCEL ALL OPEN ALGO
 * ORDERS, OPTIONALLY EXIT ALGO POSITIONS, LOCK ORDER ENGINE").
 *
 * LIVE-trading only — paper trade generation is unaffected, so the desk
 * keeps seeing what every setup WOULD have done during a halt. Does not
 * auto-reset; stays banner-visible until an admin explicitly clears it,
 * even the next trading day.
 */
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const HardKillSwitch = () => {
  const [status, setStatus] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [exitPositions, setExitPositions] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [gov, setGov] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/risk-governance/hard-kill`);
      setStatus(r.data);
    } catch {
      /* best-effort */
    }
    try {
      const g = await axios.get(`${API}/risk-governance/status`);
      if (g.data?.ok) setGov(g.data);
    } catch {
      /* best-effort */
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 20000);
    return () => clearInterval(id);
  }, [load]);

  const activate = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await axios.post(`${API}/risk-governance/hard-kill`, {
        exit_positions: exitPositions,
        activated_by: "admin",
      });
      if (!r.data?.ok) {
        setError(r.data?.message || "Activation failed");
      } else {
        setResult(r.data);
      }
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || "Activation failed");
    } finally {
      setBusy(false);
      setShowConfirm(false);
    }
  };

  const clear = async () => {
    setBusy(true);
    setError(null);
    try {
      await axios.post(`${API}/risk-governance/hard-kill/clear`, { cleared_by: "admin" });
      setResult(null);
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || "Clear failed");
    } finally {
      setBusy(false);
    }
  };

  const active = !!status?.active;
  const enforce = gov?.enforce_mode === "enforce";
  const loss = gov?.daily_loss || {};
  const rs = (v) => (v == null ? "—" : `₹${Math.round(v).toLocaleString("en-IN")}`);
  const blockedToday = (gov?.events_today || []).filter((e) => e.blocked).length;
  const wouldToday = (gov?.events_today || []).filter((e) => !e.blocked).length;

  return (
    <>
      {gov?.daily_loss_limit_hit && !active && (
        <div
          data-testid="daily-loss-limit-banner"
          className="border-l-4 px-4 py-2 text-xs"
          style={{ borderLeftColor: "var(--bear)", background: "rgba(228, 90, 90, 0.08)" }}
        >
          <strong style={{ color: "var(--bear)" }}>Daily loss limit reached</strong>
          {" "}— day P&L {rs(loss.day_pnl_rs)} vs limit −{rs(loss.limit_rs)}.{" "}
          {enforce
            ? "No new live entries today; open positions keep their normal exits. Paper trading continues."
            : "Shadow mode: live entries are NOT being blocked (set risk_governance.enforce_mode = enforce)."}
        </div>
      )}
      {active && (
        <div
          data-testid="hard-kill-active-banner"
          className="border-l-4 px-4 py-3 flex items-start gap-3"
          style={{
            borderLeftColor: "var(--bear)",
            background: "rgba(228, 90, 90, 0.1)",
            color: "var(--text-primary)",
          }}
        >
          <div className="pt-0.5" style={{ color: "var(--bear)" }}>
            <Skull size={18} />
          </div>
          <div className="flex-1 min-w-0 space-y-1">
            <strong
              className="text-[11px] uppercase tracking-[0.18em] font-mono-data"
              style={{ color: "var(--bear)" }}
            >
              Hard Kill Active — all live entries blocked
            </strong>
            <div className="text-xs text-muted-1">
              Activated by {status.activated_by || "admin"}
              {status.activated_at ? ` at ${status.activated_at}` : ""}. Pending live BUY orders
              were cancelled{status.exit_positions_requested ? " and open positions were exited." : "; open positions were left in place (exit was not requested)."}
              {" "}Paper trading continues unaffected. Clear manually when ready to resume live trading.
            </div>
            {result?.cancelled?.length > 0 && (
              <div className="text-[10px] text-muted-3 font-mono-data">
                Cancelled: {result.cancelled.length} · Exited: {result.exited?.length || 0}
              </div>
            )}
          </div>
          <button
            onClick={clear}
            disabled={busy}
            data-testid="hard-kill-clear-btn"
            className="border px-3 py-1.5 text-[10px] uppercase tracking-wider font-mono-data hover:bg-white/5 disabled:opacity-50 inline-flex items-center gap-1"
            style={{ borderColor: "var(--bear)", color: "var(--bear)" }}
          >
            {busy ? <Loader2 size={11} className="animate-spin" /> : null}
            Clear Kill Switch
          </button>
        </div>
      )}

      {!active && (
        <div className="flex justify-end items-center gap-3">
          {gov && (
            <span
              className="text-[10px] font-mono-data text-muted-2"
              data-testid="risk-gov-status"
              title="Risk Governance (Admin → Risk Governance). Day P&L = Kite day positions (fallback: filled live trades' net P&L)."
            >
              Risk gates: <span style={{ color: enforce ? "var(--bull)" : "var(--muted-2, #999)" }}>
                {enforce ? "ENFORCED" : "SHADOW"}</span>
              {" · "}Day P&L {rs(loss.day_pnl_rs)}
              {loss.limit_rs ? ` / limit −${rs(loss.limit_rs)}` : " · no ₹ limit"}
              {blockedToday ? ` · ${blockedToday} blocked today` : ""}
              {wouldToday ? ` · ${wouldToday} would-block today` : ""}
            </span>
          )}
          <button
            onClick={() => setShowConfirm(true)}
            data-testid="hard-kill-open-btn"
            title="Global Emergency Kill Switch — LIVE trading only"
            className="border px-3 py-1.5 text-[10px] uppercase tracking-wider font-mono-data hover:bg-white/5 inline-flex items-center gap-1.5"
            style={{ borderColor: "var(--bear)", color: "var(--bear)" }}
          >
            <ShieldAlert size={13} />
            Emergency Kill Switch
          </button>
        </div>
      )}

      {error && (
        <div className="text-xs text-[var(--bear)]" data-testid="hard-kill-error">
          {error}
        </div>
      )}

      {showConfirm && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4"
          data-testid="hard-kill-confirm-modal"
        >
          <div
            className="w-full max-w-md border border-grid p-5 space-y-4"
            style={{ background: "var(--bg-primary, #111)" }}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-2" style={{ color: "var(--bear)" }}>
                <Skull size={18} />
                <h3 className="font-heading text-lg">Activate Global Hard Kill?</h3>
              </div>
              <button onClick={() => setShowConfirm(false)} className="text-muted-3 hover:text-text-primary">
                <X size={16} />
              </button>
            </div>
            <div className="text-xs text-muted-1 space-y-2">
              <p>This immediately, across ALL setups (stocks + commodities):</p>
              <ul className="list-disc pl-4 space-y-1">
                <li>Blocks every new LIVE entry (paper trading is unaffected).</li>
                <li>Cancels every unfilled live BUY order at the broker.</li>
                <li>
                  Optionally exits every open LIVE position at market, using each setup's own
                  safe exit path.
                </li>
              </ul>
              <p>This does not auto-reset — you must clear it manually, even the next day.</p>
            </div>
            <label className="flex items-center gap-2 text-xs cursor-pointer">
              <input
                type="checkbox"
                checked={exitPositions}
                onChange={(e) => setExitPositions(e.target.checked)}
                data-testid="hard-kill-exit-positions-checkbox"
              />
              Also exit all open live positions now (not just cancel pending orders)
            </label>
            <div className="flex justify-end gap-2 pt-2">
              <button
                onClick={() => setShowConfirm(false)}
                className="border border-grid px-3 py-1.5 text-[10px] uppercase tracking-wider font-mono-data hover:bg-white/5"
              >
                Cancel
              </button>
              <button
                onClick={activate}
                disabled={busy}
                data-testid="hard-kill-confirm-btn"
                className="border px-3 py-1.5 text-[10px] uppercase tracking-wider font-mono-data hover:bg-white/5 disabled:opacity-50 inline-flex items-center gap-1"
                style={{ borderColor: "var(--bear)", color: "var(--bear)", background: "rgba(228,90,90,0.1)" }}
              >
                {busy ? <Loader2 size={11} className="animate-spin" /> : <Skull size={11} />}
                Activate Hard Kill
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
